import React, { useEffect, useState } from 'react';
import { Search, Edit, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from '../../hooks/use-toast';
import { adminAPI } from '../../services/api';
import { shipmentStatuses, statusColors } from '../../data/constants';
import AdminLayout from '../../components/AdminLayout';

const AdminShipments = () => {
  const [shipments, setShipments] = useState([]);
  const [filteredShipments, setFilteredShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');

  useEffect(() => {
    loadShipments();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const filtered = shipments.filter(
        (s) =>
          (s.tracking_number || '').toLowerCase().includes(q) ||
          (s.sender_name || '').toLowerCase().includes(q) ||
          (s.receiver_name || '').toLowerCase().includes(q) ||
          (s.sender_phone || '').includes(searchQuery) ||
          (s.receiver_phone || '').includes(searchQuery) ||
          (s.sender_email || '').toLowerCase().includes(q)
      );
      setFilteredShipments(filtered);
    } else {
      setFilteredShipments(shipments);
    }
  }, [searchQuery, shipments]);

  const loadShipments = async () => {
    try {
      const data = await adminAPI.getAllShipments();
      setShipments(data);
      setFilteredShipments(data);
    } catch (error) {
      toast({ title: "Error", description: "Failed to load shipments", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus) return;
    try {
      await adminAPI.updateShipmentStatus(selectedShipment.tracking_number, newStatus);
      toast({ title: "Status Updated", description: `Shipment status updated to: ${newStatus}` });
      setStatusDialogOpen(false);
      loadShipments();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
    }
  };

  const handleDelete = async (trackingNumber) => {
    if (!window.confirm('Are you sure you want to delete this shipment?')) return;
    try {
      await adminAPI.deleteShipment(trackingNumber);
      toast({ title: "Shipment Deleted", description: "Shipment has been deleted successfully" });
      loadShipments();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete shipment", variant: "destructive" });
    }
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-gray-900" data-testid="shipments-title">Shipment Management</h1>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              data-testid="shipment-search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by tracking number, name, phone, or email..."
              className="pl-10 h-12"
            />
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#D62828]"></div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Tracking #</th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Sender</th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Receiver</th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Route</th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Service</th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Price</th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredShipments.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-4 py-12 text-center text-gray-500">No shipments found</td>
                    </tr>
                  ) : (
                    filteredShipments.map((shipment) => (
                      <tr key={shipment.tracking_number} className="hover:bg-gray-50 cursor-pointer" onClick={() => { setSelectedShipment(shipment); setDetailDialogOpen(true); }}>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="font-semibold text-gray-900 text-sm">{shipment.tracking_number}</div>
                          <div className="text-xs text-gray-500">{new Date(shipment.created_at).toLocaleDateString()}</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm font-medium text-gray-900">{shipment.sender_name || 'N/A'}</div>
                          <div className="text-xs text-gray-500">{shipment.sender_phone || ''}</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm font-medium text-gray-900">{shipment.receiver_name || 'N/A'}</div>
                          <div className="text-xs text-gray-500">{shipment.receiver_phone || ''}</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-xs text-gray-900"><span className="font-medium">From:</span> {shipment.sender_address || 'N/A'}</div>
                          <div className="text-xs text-gray-900 mt-1"><span className="font-medium">To:</span> {shipment.receiver_address || 'N/A'}</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm font-medium text-gray-900">{shipment.service_type || 'N/A'}</div>
                          <div className="text-xs text-gray-500">via {shipment.travel_mode || 'N/A'}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm font-bold text-[#D62828]">
                            {shipment.price ? `₱${Number(shipment.price).toLocaleString()}` : '—'}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColors[shipment.status] || 'bg-gray-100 text-gray-800'}`}>
                            {shipment.status}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`edit-status-${shipment.tracking_number}`}
                              onClick={() => {
                                setSelectedShipment(shipment);
                                setNewStatus(shipment.status);
                                setStatusDialogOpen(true);
                              }}
                              className="text-blue-600 border-blue-600 hover:bg-blue-50"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`delete-${shipment.tracking_number}`}
                              onClick={() => handleDelete(shipment.tracking_number)}
                              className="text-red-600 border-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Shipment Detail Dialog */}
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Shipment Details — {selectedShipment?.tracking_number}</DialogTitle>
            </DialogHeader>
            {selectedShipment && (
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Sender</p>
                    <p className="font-semibold">{selectedShipment.sender_name || 'N/A'}</p>
                    <p className="text-gray-600">{selectedShipment.sender_phone || ''}</p>
                    <p className="text-gray-600">{selectedShipment.sender_email || ''}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Receiver</p>
                    <p className="font-semibold">{selectedShipment.receiver_name || 'N/A'}</p>
                    <p className="text-gray-600">{selectedShipment.receiver_phone || ''}</p>
                    <p className="text-gray-600">{selectedShipment.receiver_email || ''}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Origin</p>
                    <p className="font-semibold">{selectedShipment.sender_address || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Destination</p>
                    <p className="font-semibold">{selectedShipment.receiver_address || 'N/A'}</p>
                  </div>
                </div>
                {selectedShipment.item_description && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Item Description</p>
                    <p className="font-semibold">{selectedShipment.item_description}</p>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Service Type</p>
                    <p className="font-semibold">{selectedShipment.service_type || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Travel Mode</p>
                    <p className="font-semibold">{selectedShipment.travel_mode || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Delivery Type</p>
                    <p className="font-semibold">{selectedShipment.delivery_type || 'N/A'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Box Type</p>
                    <p className="font-semibold capitalize">{selectedShipment.box_type || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Dimensions</p>
                    <p className="font-semibold">{selectedShipment.length || 0}x{selectedShipment.width || 0}x{selectedShipment.height || 0} cm</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Weight</p>
                    <p className="font-semibold">{selectedShipment.weight || 0} kg</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Package Value</p>
                    <p className="font-semibold">{selectedShipment.package_value ? `₱${Number(selectedShipment.package_value).toLocaleString()}` : '—'}</p>
                  </div>
                  <div className="bg-[#D62828]/5 border border-[#D62828]/20 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Shipping Price</p>
                    <p className="text-xl font-bold text-[#D62828]">
                      {selectedShipment.price ? `₱${Number(selectedShipment.price).toLocaleString()}` : '—'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Status Update Dialog */}
        <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Shipment Status</DialogTitle>
              <DialogDescription>
                Update status for: {selectedShipment?.tracking_number}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Select New Status</label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {shipmentStatuses.map((status) => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
                <Button
                  data-testid="update-status-btn"
                  onClick={handleUpdateStatus}
                  className="bg-[#D62828] hover:bg-[#B01F1F] text-white"
                >
                  Update Status
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminShipments;
